import { getApiErrorI18nKey, useCorpusDocumentDetail } from "@noa/api-client";
import type { CorpusChunk, CorpusDocumentRecord } from "@noa/api-core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthenticatedImage } from "~/components/chat/chat-message-authenticated-image";
import { Alert, DataTable, Descriptions, Dialog, Spin, Tabs, Tag, Text, type DataTableColumn } from "~/components/ui";
import { chunkKindKey, sourceLabelKey } from "~/utils/corpus-admin";

/** Chunks shown per page of the segments tab. */
const CHUNK_PAGE_SIZE = 20;

interface CorpusDocumentDialogProps {
  /** The open document's source URL; null when the dialog is closed. */
  sourceUrl: string | null;
  name: string;
  onClose: () => void;
  /** Whether the viewer may delete: only then is a file shared by types warned of what deleting it removes. */
  canDelete: boolean;
}

const fieldValue = (value: unknown): string =>
  Array.isArray(value)
    ? value.join(", ")
    : typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value);

function RecordDetails({ record }: { record: CorpusDocumentRecord }) {
  const { t, i18n } = useTranslation("admin");
  const fields = Object.entries(record.fields);

  return (
    <div className="flex flex-col gap-3">
      <Descriptions
        bordered
        size="small"
        column={1}
        items={[
          { key: "type", label: t("vectorstoreAdmin.detail.type"), children: record.document_type ?? "—" },
          {
            key: "source",
            label: t("vectorstoreAdmin.detail.sourceSystem"),
            children: record.source_system
              ? t(sourceLabelKey(record.source_system), { defaultValue: record.source_system })
              : "—",
          },
          {
            key: "chunks",
            label: t("vectorstoreAdmin.detail.chunkCount"),
            children: record.chunk_count.toLocaleString(i18n.language),
          },
          record.summary
            ? { key: "summary", label: t("vectorstoreAdmin.detail.summary"), children: record.summary }
            : null,
        ]}
      />
      <Text strong>{t("vectorstoreAdmin.detail.fields")}</Text>
      {fields.length > 0 ? (
        <Descriptions
          bordered
          size="small"
          column={1}
          items={fields.map(([name, value]) => ({ key: name, label: name, children: fieldValue(value) }))}
        />
      ) : (
        <Text tone="muted">{t("vectorstoreAdmin.detail.noFields")}</Text>
      )}
    </div>
  );
}

/** A document's chunks, as the agent retrieves them, and its records' metadata. */
export function CorpusDocumentDialog({ sourceUrl, name, onClose, canDelete }: CorpusDocumentDialogProps) {
  const { t, i18n } = useTranslation("admin");
  const [page, setPage] = useState(1);
  // The last document opened stays shown while the dialog fades out
  const [shown, setShown] = useState<{ sourceUrl: string; name: string } | null>(null);
  if (sourceUrl !== null && (shown?.sourceUrl !== sourceUrl || shown.name !== name)) {
    setShown({ sourceUrl, name });
  }
  const { detail, isLoading, isFetching, isError, error } = useCorpusDocumentDetail(
    shown?.sourceUrl ?? null,
    (page - 1) * CHUNK_PAGE_SIZE,
    CHUNK_PAGE_SIZE
  );

  const afterClose = () => {
    setShown(null);
    setPage(1);
  };

  const columns: DataTableColumn<CorpusChunk>[] = [
    {
      key: "location",
      title: "#",
      width: 170,
      render: (chunk) => (
        <div className="flex flex-col gap-1">
          <Text strong>{chunk.index + 1}</Text>
          <Text tone="muted" className="text-xs">
            {chunk.location_information || t("vectorstoreAdmin.detail.noLocation")}
          </Text>
          <div className="flex flex-wrap gap-1">
            <Tag>{t(chunkKindKey(chunk.source_type ?? "text"), { defaultValue: chunk.source_type })}</Tag>
            {chunk.image_type && <Tag>{chunk.image_type}</Tag>}
          </div>
        </div>
      ),
    },
    {
      key: "content",
      title: t("vectorstoreAdmin.detail.content"),
      render: (chunk) => (
        <div className="flex flex-col gap-2">
          {chunk.has_image && (
            <AuthenticatedImage
              url={`/api/images/${chunk.unique}`}
              alt={t("vectorstoreAdmin.detail.image", { index: chunk.index + 1 })}
            />
          )}
          <div className="whitespace-pre-wrap break-words text-sm">{chunk.text}</div>
        </div>
      ),
    },
  ];

  const typesAtUrl = new Set(detail?.records.map((record) => record.document_type)).size;

  return (
    <Dialog
      open={sourceUrl !== null}
      onCancel={onClose}
      afterClose={afterClose}
      title={shown?.name}
      footer={null}
      width={960}
      classNames={{ body: "max-h-[70vh] overflow-y-auto" }}
    >
      <Text tone="muted" className="mb-3 block break-all text-xs">
        {shown?.sourceUrl}
      </Text>
      {isError && <Alert type="error" showIcon message={t(getApiErrorI18nKey(error))} />}
      {isLoading && <Spin spinning className="block py-8" />}
      {detail && (
        <>
          {typesAtUrl > 1 && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={t("vectorstoreAdmin.detail.shared", { count: typesAtUrl })}
              description={canDelete ? t("vectorstoreAdmin.detail.sharedDelete") : undefined}
            />
          )}
          <Tabs
            destroyOnHidden
            items={[
              {
                key: "chunks",
                label: t("vectorstoreAdmin.detail.chunksTab", {
                  formattedCount: detail.chunk_total.toLocaleString(i18n.language),
                }),
                children: (
                  <DataTable
                    columns={columns}
                    data={detail.chunks}
                    rowKey={(chunk) => chunk.unique}
                    size="small"
                    loading={isFetching}
                    pagination={{
                      pageSize: CHUNK_PAGE_SIZE,
                      current: page,
                      total: detail.chunk_total,
                      onChange: setPage,
                      hideOnSinglePage: true,
                    }}
                  />
                ),
              },
              {
                key: "metadata",
                label: t("vectorstoreAdmin.detail.metadataTab"),
                children: (
                  <div className="flex flex-col gap-6">
                    {detail.records.length === 0 && <Text tone="muted">{t("vectorstoreAdmin.detail.noRecord")}</Text>}
                    {detail.records.map((record) => (
                      <RecordDetails key={record.unique} record={record} />
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </Dialog>
  );
}
